import React, { useState, useContext, useEffect } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { AuthContext } from '@/App';
import axios from 'axios';
import { 
  House, Buildings, Users, GraduationCap, BookOpen, ClipboardText, ChartBar,
  CurrencyInr, Certificate, Briefcase, Building, ShieldCheck, ClockCounterClockwise,
  Gear, SignOut, CaretLeft, CaretRight, MagnifyingGlass, Bell, CaretDown, User,
  FileText, Scroll, UserCircle, Pulse, GearSix
} from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const menuItems = [
  { 
    section: 'Main',
    items: [
      { icon: House, label: 'Dashboard', path: '/admin/dashboard', id: 'dashboard' },
    ]
  },
  {
    section: 'Management',
    items: [
      { icon: Buildings, label: 'Franchise', path: '/admin/franchise', id: 'franchise' },
      { icon: Users, label: 'Students', path: '/admin/students', id: 'students' },
      { icon: GraduationCap, label: 'Courses', path: '/admin/courses', id: 'courses' },
      { icon: Building, label: 'Employers', path: '/admin/employers', id: 'employers' },
    ]
  },
  {
    section: 'Education',
    items: [
      { icon: BookOpen, label: 'LMS', path: '/admin/lms', id: 'lms' },
      { icon: ClipboardText, label: 'Exams', path: '/admin/exams', id: 'exams' },
      { icon: FileText, label: 'Question Bank', path: '/admin/question-bank', id: 'question-bank' },
      { icon: Certificate, label: 'Certificates', path: '/admin/certificates', id: 'certificates' },
      { icon: GearSix, label: 'Certificate Settings', path: '/admin/certificate-settings', id: 'certificate-settings' },
    ]
  },
  {
    section: 'Business',
    items: [
      { icon: Briefcase, label: 'Placements', path: '/admin/placements', id: 'placements' },
      { icon: CurrencyInr, label: 'Finance', path: '/admin/finance', id: 'finance' },
      { icon: ChartBar, label: 'Reports', path: '/admin/reports', id: 'reports' },
    ]
  },
  {
    section: 'System',
    items: [
      { icon: ShieldCheck, label: 'Roles', path: '/admin/roles', id: 'roles' },
      { icon: UserCircle, label: 'Sub-Admins', path: '/admin/sub-admins', id: 'sub-admins' },
      { icon: Pulse, label: 'Activity Logs', path: '/admin/logs', id: 'logs' },
      { icon: Gear, label: 'Settings', path: '/admin/settings', id: 'settings' },
    ]
  }
];

const AdminLayout = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  
  const sidebarWidth = sidebarCollapsed ? 80 : 280;

  // Check if user is sub-admin
  const subAdminInfo = localStorage.getItem('sub_admin_info');
  const isSubAdmin = !!subAdminInfo;
  const parsedSubAdminInfo = isSubAdmin ? JSON.parse(subAdminInfo) : null;

  // Fetch notifications
  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/admin/notifications`);
      setNotifications(response.data?.notifications || [
        { id: 1, title: 'New partner application', time: '5 min ago', unread: true },
        { id: 2, title: 'Payment received', time: '1 hour ago', unread: true },
        { id: 3, title: 'Student enrolled', time: '2 hours ago', unread: false },
      ]);
    } catch (error) {
      // Use mock notifications if API fails
      setNotifications([
        { id: 1, title: 'New partner application', time: '5 min ago', unread: true },
        { id: 2, title: 'Payment received', time: '1 hour ago', unread: true },
        { id: 3, title: 'Student enrolled', time: '2 hours ago', unread: false },
      ]);
    }
  };

  // Global search
  const handleGlobalSearch = async (query) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/admin/search?q=${encodeURIComponent(query)}`);
      setSearchResults(response.data?.results || []);
      setShowSearchResults(true);
    } catch (error) {
      setSearchResults([]);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('sub_admin_info');
    logout();
    navigate('/auth/login');
  };

  const isActivePath = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const unreadCount = notifications.filter(n => n.unread).length;

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Sidebar */}
      <motion.aside
        initial={false}
        animate={{ width: sidebarWidth }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        className="fixed left-0 top-0 h-screen bg-slate-950 border-r border-slate-800 flex flex-col z-50"
        data-testid="admin-sidebar"
      >
        {/* Logo */}
        <div className="h-16 flex items-center px-5 border-b border-slate-800/50">
          <Link to="/admin/dashboard" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden">
              <img src="/iqraskills-logo.png" alt="IqraSkills" className="w-full h-full object-contain" />
            </div>
            <AnimatePresence>
              {!sidebarCollapsed && (
                <motion.span
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  className="text-xl font-bold text-white tracking-tight"
                >
                  Iqra Skills
                </motion.span>
              )}
            </AnimatePresence>
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-3 scrollbar-thin scrollbar-thumb-slate-700">
          {menuItems.map((section, idx) => {
            // Filter out System items (Roles, Sub-Admins) for sub-admins
            let filteredItems = section.items;
            if (isSubAdmin && section.section === 'System') {
              filteredItems = section.items.filter(item => 
                item.id !== 'roles' && item.id !== 'sub-admins'
              );
              // If no items left in System section, skip entire section
              if (filteredItems.length === 0) return null;
            }
            
            return (
            <div key={idx} className="mb-6">
              <AnimatePresence>
                {!sidebarCollapsed && (
                  <motion.h3
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 px-3 mb-2"
                  >
                    {section.section}
                  </motion.h3>
                )}
              </AnimatePresence>
              <div className="space-y-1">
                {filteredItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = isActivePath(item.path);
                  
                  return (
                    <Link
                      key={item.id}
                      to={item.path}
                      data-testid={`sidebar-${item.id}`}
                      className={`
                        w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200
                        ${isActive 
                          ? 'bg-gradient-to-r from-blue-600/20 to-blue-500/10 text-white border border-blue-500/20' 
                          : 'text-slate-400 hover:bg-white/5 hover:text-white'
                        }
                      `}
                    >
                      <Icon 
                        weight={isActive ? 'fill' : 'regular'} 
                        className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-blue-400' : ''}`} 
                      />
                      <AnimatePresence>
                        {!sidebarCollapsed && (
                          <motion.span
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="text-sm font-medium truncate"
                          >
                            {item.label}
                          </motion.span>
                        )}
                      </AnimatePresence>
                      {isActive && !sidebarCollapsed && (
                        <motion.div
                          layoutId="sidebarActiveIndicator"
                          className="ml-auto w-1.5 h-1.5 rounded-full bg-blue-400"
                        />
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          );
          })}
        </nav>

        {/* Collapse Toggle */}
        <div className="p-3 border-t border-slate-800/50">
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-slate-400 hover:bg-white/5 hover:text-white transition-all"
            data-testid="sidebar-toggle"
          >
            {sidebarCollapsed ? (
              <CaretRight className="w-5 h-5" />
            ) : (
              <>
                <CaretLeft className="w-5 h-5" />
                <span className="text-sm font-medium">Collapse</span>
              </>
            )}
          </button>
        </div>
      </motion.aside>

      {/* Main Content */}
      <div 
        className="transition-all duration-300"
        style={{ marginLeft: sidebarWidth }}
      >
        {/* Topbar */}
        <header
          className="sticky top-0 h-16 bg-white/90 backdrop-blur-xl border-b border-slate-200/50 z-40"
          data-testid="admin-topbar"
        >
          <div className="h-full flex items-center justify-between px-6">
            {/* Search */}
            <div className="flex-1 max-w-lg relative">
              <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => handleGlobalSearch(e.target.value)}
                onFocus={() => searchQuery.length >= 2 && setShowSearchResults(true)}
                placeholder="Search students, centers, courses..."
                className="w-full pl-10 pr-4 py-2.5 bg-slate-100 border-0 rounded-xl text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:bg-white transition-all"
                data-testid="topbar-search"
              />
              
              {/* Search Results Dropdown */}
              {showSearchResults && searchResults.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-slate-200 py-2 z-50">
                  {searchResults.map((result, idx) => (
                    <div 
                      key={idx}
                      className="px-4 py-2 hover:bg-slate-50 cursor-pointer flex items-center justify-between"
                      onClick={() => {
                        setShowSearchResults(false);
                        setSearchQuery('');
                        if (result.type === 'Student') navigate('/admin/students');
                        else if (result.type === 'Center') navigate('/admin/franchise');
                        else if (result.type === 'Course') navigate('/admin/courses');
                      }}
                    >
                      <div>
                        <span className="text-slate-900">{result.name}</span>
                        <span className="text-slate-400 text-sm ml-2">{result.email || result.code}</span>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        result.type === 'Student' ? 'bg-blue-100 text-blue-600' :
                        result.type === 'Center' ? 'bg-emerald-100 text-emerald-600' :
                        'bg-purple-100 text-purple-600'
                      }`}>
                        {result.type}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              {/* Notifications */}
              <div className="relative">
                <button
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="relative p-2.5 rounded-xl text-slate-600 hover:bg-slate-100 transition-colors"
                  data-testid="topbar-notifications"
                >
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full" />
                  )}
                </button>

                <AnimatePresence>
                  {showNotifications && (
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden"
                    >
                      <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                        <h3 className="font-semibold text-slate-900">Notifications</h3>
                        {unreadCount > 0 && (
                          <span className="text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full font-medium">
                            {unreadCount} new
                          </span>
                        )}
                      </div>
                      <div className="max-h-80 overflow-y-auto">
                        {notifications.map((notif) => (
                          <div
                            key={notif.id}
                            className={`p-4 border-b border-slate-50 hover:bg-slate-50 transition-colors cursor-pointer ${
                              notif.unread ? 'bg-blue-50/50' : ''
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <div className={`w-2 h-2 rounded-full mt-2 ${notif.unread ? 'bg-blue-500' : 'bg-slate-300'}`} />
                              <div>
                                <p className="text-sm font-medium text-slate-900">{notif.title}</p>
                                <p className="text-xs text-slate-500 mt-0.5">{notif.time}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Profile Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShowProfileMenu(!showProfileMenu)}
                  className="flex items-center gap-3 p-2 rounded-xl hover:bg-slate-100 transition-colors"
                  data-testid="topbar-profile"
                >
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center shadow-lg ${
                    isSubAdmin 
                      ? 'bg-gradient-to-br from-amber-500 to-orange-600 shadow-amber-500/20' 
                      : 'bg-gradient-to-br from-blue-600 to-blue-400 shadow-blue-500/20'
                  }`}>
                    <span className="text-white font-semibold text-sm">
                      {isSubAdmin ? parsedSubAdminInfo?.name?.charAt(0) : user?.name?.charAt(0) || 'A'}
                    </span>
                  </div>
                  <div className="hidden md:block text-left">
                    <p className="text-sm font-medium text-slate-900">
                      {isSubAdmin ? parsedSubAdminInfo?.name : user?.name || 'Admin'}
                    </p>
                    <p className="text-xs text-slate-500">
                      {isSubAdmin ? parsedSubAdminInfo?.role_name || 'Sub-Admin' : 'Super Admin'}
                    </p>
                  </div>
                  <CaretDown className="w-4 h-4 text-slate-400 hidden md:block" />
                </button>

                <AnimatePresence>
                  {showProfileMenu && (
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden"
                    >
                      <div className="p-4 border-b border-slate-100">
                        <p className="font-medium text-slate-900">
                          {isSubAdmin ? parsedSubAdminInfo?.name : user?.name || 'Admin'}
                        </p>
                        <p className="text-sm text-slate-500">{user?.email}</p>
                      </div>
                      <div className="p-2">
                        <button className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-slate-600 hover:bg-slate-50 transition-colors">
                          <User className="w-4 h-4" />
                          <span className="text-sm">My Profile</span>
                        </button>
                        <Link 
                          to="/admin/settings"
                          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-slate-600 hover:bg-slate-50 transition-colors"
                        >
                          <Gear className="w-4 h-4" />
                          <span className="text-sm">Settings</span>
                        </Link>
                      </div>
                      <div className="p-2 border-t border-slate-100">
                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
                          data-testid="topbar-logout"
                        >
                          <SignOut className="w-4 h-4" />
                          <span className="text-sm">Logout</span>
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>

          {/* Click outside to close dropdowns */}
          {(showNotifications || showProfileMenu || showSearchResults) && (
            <div
              className="fixed inset-0 z-[-1]"
              onClick={() => {
                setShowNotifications(false);
                setShowProfileMenu(false);
                setShowSearchResults(false);
              }}
            />
          )}
        </header>

        {/* Page Content - Outlet renders child routes */}
        <main className="p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
